import { QueryClientProvider } from '@tanstack/react-query';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';

import { authEvents } from '@/api/auth-events';
import { queryClient } from '@/api/query-client';
import { AuthProvider } from '@/auth/AuthProvider';
import { ViewingAsProvider } from '@/auth/ViewingAsProvider';
import { ToastProvider } from '@/components/ui/Toast';
import { theme } from './theme';

/**
 * The Query developer tools are loaded on demand and only during development.
 *
 * A plain top-level import would place them in the production bundle even though
 * the `DEV` check stops them rendering — the check happens when the page runs,
 * long after the bundler has already included the code.
 */
const Devtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

/** Everything the whole app needs wrapped around it, in one place. */
export function Providers({ children }: { children: ReactNode }) {
  useEffect(() => {
    // When the API layer decides the session is over, throw away everything it
    // cached. Otherwise one organization's data could still be on screen after
    // a different person signs in.
    return authEvents.onSignedOut(() => {
      queryClient.clear();
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        {/* Applies the theme's background, text colour, focus ring and the
            reduce-motion rule to the whole document. */}
        <CssBaseline />
        <AuthProvider>
          {/* Inside the theme, because the snackbar it renders is themed, and
              around everything, because a confirmation has to outlive the
              screen that asked for it. */}
          <ViewingAsProvider>
            <ToastProvider>{children}</ToastProvider>
          </ViewingAsProvider>
        </AuthProvider>
      </ThemeProvider>
      {Devtools ? (
        <Suspense fallback={null}>
          <Devtools initialIsOpen={false} />
        </Suspense>
      ) : null}
    </QueryClientProvider>
  );
}
